/**
 * MONSTER TOWN — WHAT HAPPENS WHEN AN ERRAND LANDS. ERRAND CREW.
 *
 * `core/errands.ts` finishes the run and emits `errand:finished` with the
 * reward attached. It deliberately does not pay it: core owns no wallet and no
 * bag. This does, and it is the listener `wiring-check` was holding open under
 * "hud cheer + economy payout + npc thank-you pose".
 *
 * THE PAYOUT IS ALWAYS A GAIN. There is no path here that takes anything away,
 * charges anything, or declines. `earnExact` cannot go negative and `add`
 * cannot fail, so the worst possible outcome of finishing an errand is that a
 * reward-less errand pays nothing and still cheers (LAW #2).
 *
 * KNOWN GAP, stated rather than hidden: `ErrandReward.grants` (own a placeable
 * forever) has no home to go to — `Wallet.owned` is private and `buy()` is the
 * only thing that appends to it, and `src/core/economy.ts` is a frozen contract
 * this crew does not own. Nothing in `src/content/errands.ts` uses `grants`
 * today, and a grant that arrived would be paid as coins rather than silently
 * swallowed. Owed to the economy crew: `wallet.grant(id)`.
 */

import { bus } from '../core/events'
import { wallet } from '../core/economy'
import { inventory } from '../core/inventory'
import { getErrand } from '../core/errands'
import { save } from '../core/save'
import { app } from '../app/context'

/** What a granted-but-unpayable placeable is worth instead. Data. */
const GRANT_FALLBACK_COINS = 15

let wired = false

export function installRewards(): void {
  if (wired) return
  wired = true

  bus.on('errand:finished', (e) => {
    const def = getErrand(e.id)
    const reward = e.reward ?? def?.reward
    const at = { ...app.playerPos }

    let coins = Math.max(0, Math.round(reward?.coins ?? 0))
    for (const [itemId, count] of Object.entries(reward?.items ?? {})) {
      inventory.add(itemId, Math.max(1, Math.round(count)), `errand:${e.id}`)
    }
    // See the header: no wallet.grant() exists to call. Pay it, never drop it.
    coins += (reward?.grants?.length ?? 0) * GRANT_FALLBACK_COINS

    if (coins > 0) wallet.earnExact(coins, `errand.${e.id}`, at)

    // The cheer. A picture, a sound, and the kid's own monster celebrating —
    // three signals, no words (LAW #4).
    bus.emit('ui:toast', { icon: 'star', ms: 2400 })
    bus.emit('audio:cue', { cue: 'chime_happy' })
    bus.emit('monster:action', { action: 'cheer', seconds: 1.6 })
    if (coins > 0) bus.emit('ui:fly', { icon: 'coin', amount: coins, at })

    save.request(`errand:${e.id}`)
  })
}
